// The device's profile registry — the multi-profile-per-device model.
//
// A shared family device (one iPad, several kids) holds MANY local profiles plus
// a pointer to whoever's racing right now. Each profile is independently minted
// on the server (its own playerId + signed playerToken), so the server never
// needs to know two profiles share a browser — this file is pure client glue.
//
// Anonymous-first: a profile starts local-only (no server identity) and is
// lazily upgraded to a server profile the first time its owner saves a score.
import { profileToken, type ProfileIdentity } from "./api";

const KEY = "bibleracers:profiles";
const LEGACY_NAME_KEY = "br:name"; // single-name storage from before profiles
const NUDGE_KEY = "bibleracers:claim-nudged"; // playerIds already shown the save nudge

/** Kid-friendly avatar choices (also used as the profile's visual identity). */
export const AVATARS = [
  "🦊", "🐢", "🦁", "🐬", "🦉", "🐝",
  "🐙", "🦄", "🐧", "🐸", "🦖", "🐼",
];

/** Keep the "Who's racing?" chooser sane and limit abuse on a shared device. */
export const MAX_PROFILES = 6;

export interface LocalProfile {
  /** Stable client id — present even before a server identity is minted. */
  localId: string;
  /** Server identity; null until the profile is upgraded (first save). */
  playerId: string | null;
  playerToken: string | null;
  displayName: string;
  avatar: string;
  /** Claimed vanity handle, if any. */
  handle: string | null;
}

interface Store {
  profiles: LocalProfile[];
  activeId: string | null;
}

function uid(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `p_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Store;
      if (Array.isArray(parsed.profiles)) return parsed;
    }
  } catch {
    /* unreadable / blocked — fall through to migration */
  }
  return migrateLegacy();
}

/** Seed the registry from the old single-name key, so returning players keep
 *  their racer name. Runs once; the result is persisted on first write. */
function migrateLegacy(): Store {
  let name = "";
  try {
    name = localStorage.getItem(LEGACY_NAME_KEY) ?? "";
  } catch {
    /* ignore */
  }
  if (!name.trim()) return { profiles: [], activeId: null };
  const profile: LocalProfile = {
    localId: uid(),
    playerId: null,
    playerToken: null,
    displayName: name.trim().slice(0, 16),
    avatar: AVATARS[0],
    handle: null,
  };
  const store: Store = { profiles: [profile], activeId: profile.localId };
  write(store);
  return store;
}

function write(store: Store): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    /* storage blocked — registry just won't persist */
  }
}

export function listProfiles(): LocalProfile[] {
  return read().profiles;
}

export function getActiveProfile(): LocalProfile | null {
  const store = read();
  return store.profiles.find((p) => p.localId === store.activeId) ?? null;
}

export function setActive(localId: string | null): void {
  const store = read();
  write({ ...store, activeId: localId });
}

/** Create a new local-only profile and make it active. Caller upgrades it to a
 *  server profile lazily (see ensureServerProfile). Returns null at the cap. */
export function addProfile(displayName: string, avatar: string): LocalProfile | null {
  const store = read();
  if (store.profiles.length >= MAX_PROFILES) return null;
  const profile: LocalProfile = {
    localId: uid(),
    playerId: null,
    playerToken: null,
    displayName: displayName.trim().slice(0, 16) || "Racer",
    avatar: avatar || AVATARS[store.profiles.length % AVATARS.length],
    handle: null,
  };
  write({ profiles: [...store.profiles, profile], activeId: profile.localId });
  return profile;
}

export function updateProfile(localId: string, patch: Partial<LocalProfile>): void {
  const store = read();
  write({
    ...store,
    profiles: store.profiles.map((p) =>
      p.localId === localId ? { ...p, ...patch } : p,
    ),
  });
}

export function removeProfile(localId: string): void {
  const store = read();
  const profiles = store.profiles.filter((p) => p.localId !== localId);
  const activeId =
    store.activeId === localId ? (profiles[0]?.localId ?? null) : store.activeId;
  write({ profiles, activeId });
}

/** Ensure a profile has a server identity, minting one on first need. On failure
 *  (no DB / offline) the profile is returned unchanged — the caller can still
 *  post an unlinked score. */
export async function ensureServerProfile(localId: string): Promise<LocalProfile> {
  const current = read().profiles.find((p) => p.localId === localId);
  if (!current) throw new Error("Profile not found.");
  if (current.playerToken && current.playerId) return current;

  const id = await profileToken(current.displayName, current.avatar);
  updateProfile(localId, { playerId: id.playerId, playerToken: id.playerToken });
  return { ...current, playerId: id.playerId, playerToken: id.playerToken };
}

/** Store a claimed handle on a profile (after claim-profile succeeds). */
export function applyHandle(localId: string, handle: string): void {
  updateProfile(localId, { handle });
}

/** Add (or refresh) a profile from a restore-profile response, dedup by playerId,
 *  and make it active. Returns the resulting local profile. */
export function importIdentity(id: ProfileIdentity): LocalProfile {
  const store = read();
  const existing = store.profiles.find((p) => p.playerId === id.playerId);
  if (existing) {
    const merged: LocalProfile = {
      ...existing,
      playerToken: id.playerToken,
      displayName: id.displayName,
      avatar: id.avatar,
      handle: id.handle ?? existing.handle,
    };
    write({
      profiles: store.profiles.map((p) => (p.localId === existing.localId ? merged : p)),
      activeId: existing.localId,
    });
    return merged;
  }
  const profile: LocalProfile = {
    localId: uid(),
    playerId: id.playerId,
    playerToken: id.playerToken,
    displayName: id.displayName,
    avatar: id.avatar,
    handle: id.handle ?? null,
  };
  write({ profiles: [...store.profiles, profile], activeId: profile.localId });
  return profile;
}

/** Whether the one-time "save your racer" nudge has already been shown for this
 *  profile. It appears once — right after a player earns their first badge — so
 *  we lift the recovery-setup rate without ever nagging. */
export function hasSeenClaimNudge(playerId: string): boolean {
  try {
    const arr = JSON.parse(localStorage.getItem(NUDGE_KEY) ?? "[]");
    return Array.isArray(arr) && arr.includes(playerId);
  } catch {
    return false;
  }
}

export function markClaimNudgeSeen(playerId: string): void {
  try {
    const arr = JSON.parse(localStorage.getItem(NUDGE_KEY) ?? "[]");
    const seen: string[] = Array.isArray(arr) ? arr : [];
    if (!seen.includes(playerId)) {
      seen.push(playerId);
      localStorage.setItem(NUDGE_KEY, JSON.stringify(seen));
    }
  } catch {
    /* storage blocked — the nudge may show again, which is acceptable */
  }
}
